import { TextArea } from 'common/components';
import { WorkflowConfigFormData } from 'pages/WorkflowBuilder/AgenticWorkflowBuilder';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { formatGenericTag } from 'pages/WorkflowBuilder/workflowNodeUtils';

export const GenericPromptsTab = ({ isActive }: { isActive: boolean }) => {
    const {
        register,
        getValues,
        clearErrors,
        formState: { errors },
    } = useFormContext<WorkflowConfigFormData>();

    const registerPrompt = (fieldName: `genericPrompts.${string}.value`, required = true) => {
        const reg = register(fieldName, required ? { required: 'Prompt is required' } : {});
        return {
            ...reg,
            onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                reg.onChange(e);
                if (e.target.value.trim()) clearErrors(fieldName);
            },
        };
    };

    const DynamicPrompts = useMemo(() => {
        const genericTags = getValues('genericTags') ?? [];
        const genericPrompts = getValues('genericPrompts');
        const sortedTags = [...genericTags].filter((tag) => tag?.value).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        return [
            { ...genericPrompts.general_prompt, name: 'general_prompt' },
            ...sortedTags.map((tag) => ({
                name: formatGenericTag(tag.value),
                // @ts-ignore
                promptName: tag.value,
                ...genericPrompts[formatGenericTag(tag.value)],
            })),
        ];
    }, [isActive]);

    const StaticPrompts = useMemo(() => {
        const genericPrompts = getValues('genericPrompts');
        return [
            { ...genericPrompts.qualify_prompt, name: 'qualify_prompt' },
            { ...genericPrompts.unqualify_prompt, name: 'unqualify_prompt' },
            { ...genericPrompts.undetermined_prompt, name: 'undetermined_prompt' },
        ];
    }, [isActive]);

    return (
        <div className='flex flex-col gap-lg pt-xl'>
            <div className='text-gray-700'>
                <p className='mb-sm'>
                    Tag prompts — Use the prompts below to tell the AI how to extract and evaluate each tag from the
                    call transcript. The General Prompt applies to all tags; each custom tag can have its own prompt for
                    more specific instructions.
                </p>
                <p>
                    Disposition prompts — The Qualify, Unqualify, and Undetermined prompts define how the system decides
                    the final outcome of the call based on the extracted tag values.
                </p>
            </div>
            {DynamicPrompts.map((prompt) => (
                <div key={prompt.name}>
                    <TextArea
                        label={prompt.promptName}
                        error={!!errors.genericPrompts?.[prompt.name]?.value}
                        errorText={errors.genericPrompts?.[prompt.name]?.value?.message}
                        {...registerPrompt(`genericPrompts.${prompt.name}.value`)}
                    />
                </div>
            ))}
            <div style={{ borderBottom: '1px solid var(--gray-500)' }} className='mt-md mb-md' />

            <div>
                <TextArea
                    label='Sanity Check Prompt'
                    error={!!errors.genericPrompts?.sanity_check_prompt?.value}
                    errorText={errors.genericPrompts?.sanity_check_prompt?.value?.message}
                    {...registerPrompt(`genericPrompts.sanity_check_prompt.value`, false)}
                />
            </div>
            <div style={{ borderBottom: '1px solid var(--gray-500)' }} className='mt-md mb-md' />

            {StaticPrompts.map((prompt) => (
                <div key={prompt.name}>
                    <TextArea
                        label={prompt.promptName}
                        error={!!errors.genericPrompts?.[prompt.name]?.value}
                        errorText={errors.genericPrompts?.[prompt.name]?.value?.message}
                        {...registerPrompt(`genericPrompts.${prompt.name}.value`)}
                    />
                </div>
            ))}
        </div>
    );
};
